import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { deskKeys } from "@/lib/api/queryKeys";
import { deskApi, type DeskAction, type DeskMe } from "@/lib/api/desk";

/**
 * What this person may do in Desk, from the server.
 *
 * The role ceiling and the group grant live on the server, and every endpoint
 * enforces them. The interface used to show every action to everyone and let
 * the 403 explain — which teaches people that buttons randomly fail. This
 * reads the composed answer once per session and hides what would be refused.
 *
 * Before the answer arrives everything is `false`: a button that appears and
 * then vanishes is worse than one that appears a moment late.
 */
export function useDeskMe() {
  return useQuery<DeskMe>({
    queryKey: deskKeys.me(),
    queryFn: () => deskApi.me(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDeskCan(): (action: DeskAction) => boolean {
  const { data } = useDeskMe();
  return useCallback(
    (action: DeskAction) => data?.actions.includes(action) ?? false,
    [data],
  );
}
